import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import axios from '../../config/axiosInstance';
import { UserContext } from '../context/UserContext';
import { hasAnyRequiredPermission, PLANNER_RULES_WRITE_PERMISSIONS } from '../../config/routePermissions';


export default function GeneralConfigPage() {
  const { permissions } = useContext(UserContext);
  const canRead = permissions.includes('workshifts.read');
  const canWrite = hasAnyRequiredPermission(permissions, PLANNER_RULES_WRITE_PERMISSIONS);

  const [targetRestHours, setTargetRestHours] = useState('');
  const [standard6MonthlyFreeDays, setStandard6MonthlyFreeDays] = useState('');
  const [reponedor4MonthlyFreeDays, setReponedor4MonthlyFreeDays] = useState('');
  const [staleCalendarGraceMonths, setStaleCalendarGraceMonths] = useState('');
  const [savedValue, setSavedValue] = useState('');
  const [savedStandard6Value, setSavedStandard6Value] = useState('');
  const [savedReponedor4Value, setSavedReponedor4Value] = useState('');
  const [savedStaleCalendarGraceMonths, setSavedStaleCalendarGraceMonths] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadRules = async () => {
    try {
      const { data } = await axios.get('/planner_rules');
      const restValue = String(data?.target_rest_hours_between_shifts ?? '');
      const standard6Value = String(data?.standard_6_monthly_free_days ?? '');
      const reponedor4Value = String(data?.reponedor_4_monthly_free_days ?? '');
      const staleGraceValue = String(data?.stale_calendar_grace_months ?? '');
      setTargetRestHours(restValue);
      setSavedValue(restValue);
      setStandard6MonthlyFreeDays(standard6Value);
      setSavedStandard6Value(standard6Value);
      setReponedor4MonthlyFreeDays(reponedor4Value);
      setSavedReponedor4Value(reponedor4Value);
      setStaleCalendarGraceMonths(staleGraceValue);
      setSavedStaleCalendarGraceMonths(staleGraceValue);
      setError('');
    } catch (loadError) {
      setError(loadError?.response?.data?.error || 'No se pudo cargar la configuración general.');
    }
  };

  useEffect(() => {
    if (canRead) {
      loadRules();
    }
  }, [canRead]);

  const handleSave = async () => {
    try {
      const params = new URLSearchParams({
        target_rest_hours_between_shifts: targetRestHours,
        standard_6_monthly_free_days: standard6MonthlyFreeDays,
        reponedor_4_monthly_free_days: reponedor4MonthlyFreeDays,
        stale_calendar_grace_months: staleCalendarGraceMonths,
      });
      const { data } = await axios.put(`/planner_rules?${params.toString()}`);
      const restValue = String(data?.target_rest_hours_between_shifts ?? '');
      const standard6Value = String(data?.standard_6_monthly_free_days ?? '');
      const reponedor4Value = String(data?.reponedor_4_monthly_free_days ?? '');
      const staleGraceValue = String(data?.stale_calendar_grace_months ?? '');
      setTargetRestHours(restValue);
      setSavedValue(restValue);
      setStandard6MonthlyFreeDays(standard6Value);
      setSavedStandard6Value(standard6Value);
      setReponedor4MonthlyFreeDays(reponedor4Value);
      setSavedReponedor4Value(reponedor4Value);
      setStaleCalendarGraceMonths(staleGraceValue);
      setSavedStaleCalendarGraceMonths(staleGraceValue);
      setMessage('Configuración guardada.');
      setError('');
    } catch (saveError) {
      setError(saveError?.response?.data?.error || 'No se pudo guardar la configuración.');
      setMessage('');
    }
  };

  if (!canRead) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" color="error">No tienes permiso para ver esta sección.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" align="center" gutterBottom>
        Configuración General
      </Typography>

      <Box sx={{ maxWidth: 760, mx: 'auto' }}>
        <Alert severity="info" icon={<SettingsIcon fontSize="inherit" />} sx={{ mb: 3 }}>
          El planificador evita, en la mayor medida posible, asignaciones con menos descanso entre turnos que este valor.
          Si no hay una alternativa mejor, el sistema puede seguir permitiendo la excepción y marcarla como advertencia.
          También podés definir cuántos libres mensuales usa cada política general de descansos.
        </Alert>

        {!canWrite && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Tu rol solo tiene permiso de lectura para esta sección. Los valores se muestran a modo informativo.
          </Alert>
        )}

        {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
        {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

        <Paper sx={{ p: 3 }}>
          <TextField
            fullWidth
            label="Horas objetivo de descanso entre turnos"
            type="number"
            value={targetRestHours}
            onChange={(event) => setTargetRestHours(event.target.value)}
            helperText="Recomendado: 12. El sistema usa este valor para rotación automática y advertencias."
            disabled={!canWrite}
          />

          {savedValue && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Valor actual guardado: {savedValue} horas
            </Typography>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 3 }}>
            <TextField
              fullWidth
              label="Libres mensuales de standard_6"
              type="number"
              value={standard6MonthlyFreeDays}
              onChange={(event) => setStandard6MonthlyFreeDays(event.target.value)}
              helperText="Cantidad mensual de libres para la política general standard_6."
              disabled={!canWrite}
            />

            <TextField
              fullWidth
              label="Libres mensuales de reponedor_4"
              type="number"
              value={reponedor4MonthlyFreeDays}
              onChange={(event) => setReponedor4MonthlyFreeDays(event.target.value)}
              helperText="Cantidad mensual de libres para la política general reponedor_4."
              disabled={!canWrite}
            />
          </Box>

          {(savedStandard6Value || savedReponedor4Value) && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Valores actuales guardados: `standard_6` = {savedStandard6Value || '-'} libres, `reponedor_4` = {savedReponedor4Value || '-'} libres
            </Typography>
          )}

          <Divider sx={{ my: 3 }} />

          <TextField
            fullWidth
            label="Margen para calendarios abiertos vencidos (meses)"
            type="number"
            value={staleCalendarGraceMonths}
            onChange={(event) => setStaleCalendarGraceMonths(event.target.value)}
            helperText="Recomendado: 2. Si una estación tiene un calendario abierto que terminó hace más de este margen, hay que cerrarlo antes de poder crear uno nuevo."
            disabled={!canWrite}
          />

          {savedStaleCalendarGraceMonths && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Valor actual guardado: {savedStaleCalendarGraceMonths} {Number(savedStaleCalendarGraceMonths) === 1 ? 'mes' : 'meses'}
            </Typography>
          )}

          {canWrite && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button variant="contained" onClick={handleSave}>
                Guardar configuración
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
